// Health — system status dashboard. Reads /admin/health, polls every 30s,
// surfaces a tile per probed service with status / latency / message.
// Manual re-run button for after a config change.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, ToolBtn } from '@drivecars/admin-ui';
import {
  fetchAdminHealth,
  type HealthCheck,
  type HealthGroup,
  type HealthStatus,
} from '@/lib/admin-api';

const GROUP_LABEL: Record<HealthGroup, string> = {
  storage: 'Storage',
  comms: 'Communications',
  payments: 'Payments',
  platform: 'Platform',
};

const STATUS_TONE: Record<
  HealthStatus,
  { ring: string; bg: string; text: string; dot: string; label: string }
> = {
  ok: {
    ring: 'border-[#a7f3d0]',
    bg: 'bg-[#ecfdf5]',
    text: 'text-[#065f46]',
    dot: 'bg-[#10b981]',
    label: 'Healthy',
  },
  warn: {
    ring: 'border-[#fde68a]',
    bg: 'bg-[#fef9c3]',
    text: 'text-[#854d0e]',
    dot: 'bg-[#f59e0b]',
    label: 'Attention',
  },
  fail: {
    ring: 'border-[#fecaca]',
    bg: 'bg-[#fff1f2]',
    text: 'text-[#9f1239]',
    dot: 'bg-[#ef4444]',
    label: 'Failing',
  },
};

const OVERALL_LABEL: Record<HealthStatus, string> = {
  ok: 'All services healthy',
  warn: 'Some warnings — review optional config',
  fail: 'One or more services failing — operator action required',
};

export function Health() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: ({ signal }) => fetchAdminHealth(signal),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const grouped = useMemo(() => {
    const m = new Map<HealthGroup, HealthCheck[]>();
    for (const c of data?.checks ?? []) {
      const arr = m.get(c.group) ?? [];
      arr.push(c);
      m.set(c.group, arr);
    }
    return m;
  }, [data]);

  const counts = useMemo(() => {
    const out = { ok: 0, warn: 0, fail: 0 };
    for (const c of data?.checks ?? []) out[c.status] += 1;
    return out;
  }, [data]);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Health"
        sub={
          data
            ? `Last checked ${new Date(data.checkedAt).toLocaleTimeString()} · ${data.environment}`
            : 'Probing external dependencies…'
        }
        actions={
          <ToolBtn
            onClick={() => {
              void refetch();
            }}
          >
            {isFetching ? 'Probing…' : 'Re-run checks'}
          </ToolBtn>
        }
      />

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load health: {(error as Error).message}
        </div>
      ) : null}

      {data ? (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 ${STATUS_TONE[data.overall].ring} ${STATUS_TONE[data.overall].bg}`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_TONE[data.overall].dot}`}
              aria-hidden="true"
            />
            <span className={`text-[14px] font-semibold ${STATUS_TONE[data.overall].text}`}>
              {OVERALL_LABEL[data.overall]}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[11.5px] text-[color:var(--bo-mute)]">
            <span>{counts.ok} healthy</span>
            <span>·</span>
            <span>{counts.warn} warnings</span>
            <span>·</span>
            <span>{counts.fail} failing</span>
          </div>
        </div>
      ) : null}

      {isLoading && !data ? (
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-8 text-center text-[12.5px] text-[color:var(--bo-mute)]">
          Probing services…
        </div>
      ) : null}

      <div className="space-y-4">
        {(Object.keys(GROUP_LABEL) as HealthGroup[]).map((group) => {
          const checks = grouped.get(group) ?? [];
          if (checks.length === 0) return null;
          return (
            <section
              key={group}
              className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5"
            >
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
                {GROUP_LABEL[group]}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {checks.map((c) => (
                  <CheckTile key={c.name} check={c} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CheckTile({ check }: { check: HealthCheck }) {
  const tone = STATUS_TONE[check.status];
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${tone.ring} ${tone.bg}`}
    >
      <span
        className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${tone.dot}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={`text-[13px] font-semibold ${tone.text}`}>{check.name}</span>
          <span className={`text-[10.5px] uppercase tracking-[0.1em] ${tone.text}/80`}>
            {tone.label}
            {check.latencyMs != null ? (
              <span className="ms-2 tabular-nums opacity-80">{check.latencyMs}ms</span>
            ) : null}
          </span>
        </div>
        {check.message ? (
          <div className={`mt-1 text-[12px] ${tone.text}/85`}>{check.message}</div>
        ) : null}
      </div>
    </div>
  );
}
